import { useState, useEffect, useRef, useCallback } from 'react';
import { IconRefresh } from '@tabler/icons-react';
import TopNav from '../components/TopNav';

// Node type with mutable position
interface GraphNode {
  id: string;
  label: string;
  source: string;
  x: number;
  y: number;
  size: number;
}

// Initial mock data - Rooke Family Office business intelligence
const INITIAL_NODES: GraphNode[] = [
  // Panago Pizza nodes (red cluster)
  { id: 'm1', label: 'Panago Q4 franchise revenue $42M', source: 'panago', x: 150, y: 120, size: 12 },
  { id: 'm2', label: 'New franchise application - Surrey', source: 'panago', x: 220, y: 180, size: 8 },
  { id: 'm3', label: 'Digital ordering up 34% YoY', source: 'panago', x: 180, y: 250, size: 10 },
  { id: 'm4', label: 'Franchisee satisfaction survey 4.2/5', source: 'panago', x: 280, y: 140, size: 7 },
  { id: 'm5', label: 'Supply chain cost optimization', source: 'panago', x: 120, y: 200, size: 9 },
  { id: 'm6', label: 'Mobile app 2.0 launch metrics', source: 'panago', x: 250, y: 90, size: 8 },
  // Phantom Screens nodes (blue cluster)
  { id: 'm7', label: 'Phantom dealer network 245 total', source: 'phantom', x: 450, y: 150, size: 11 },
  { id: 'm8', label: 'US Pacific expansion +12 dealers', source: 'phantom', x: 520, y: 200, size: 9 },
  { id: 'm9', label: 'Motorized screens 12.5% YoY growth', source: 'phantom', x: 480, y: 280, size: 10 },
  { id: 'm10', label: 'Ontario dealer performance review', source: 'phantom', x: 380, y: 220, size: 7 },
  { id: 'm11', label: 'Manufacturing capacity at 82.5%', source: 'phantom', x: 550, y: 120, size: 8 },
  { id: 'm12', label: 'Lead time reduced to 8.2 days', source: 'phantom', x: 420, y: 90, size: 6 },
  // Apex Food Source nodes (green cluster)
  { id: 'm13', label: 'Apex client revenue $67M annual', source: 'apex', x: 700, y: 180, size: 12 },
  { id: 'm14', label: 'Food service distributors 35% share', source: 'apex', x: 750, y: 250, size: 9 },
  { id: 'm15', label: 'SQF Level 3 certification renewed', source: 'apex', x: 650, y: 130, size: 8 },
  { id: 'm16', label: 'Frozen foods line capacity 78%', source: 'apex', x: 780, y: 140, size: 7 },
  { id: 'm17', label: 'Organic product line proposal', source: 'apex', x: 680, y: 300, size: 10 },
  { id: 'm18', label: 'Retail private label growth +8.1%', source: 'apex', x: 720, y: 90, size: 8 },
  // Holdco/Corporate nodes (purple cluster)
  { id: 'm19', label: 'Portfolio EBITDA $31.4M combined', source: 'holdco', x: 320, y: 350, size: 11 },
  { id: 'm20', label: 'Q4 board meeting minutes', source: 'holdco', x: 400, y: 380, size: 9 },
  { id: 'm21', label: 'Cross-OpCo synergy initiatives', source: 'holdco', x: 250, y: 400, size: 8 },
  { id: 'm22', label: 'Capital allocation 2025 plan', source: 'holdco', x: 350, y: 420, size: 10 },
  // Market Intelligence nodes (orange)
  { id: 'm23', label: 'QSR industry trends Q4 2024', source: 'market', x: 550, y: 380, size: 7 },
  { id: 'm24', label: 'Home improvement market outlook', source: 'market', x: 620, y: 420, size: 8 },
  { id: 'm25', label: 'Food manufacturing M&A activity', source: 'market', x: 480, y: 450, size: 6 },
  // Financial nodes (cyan)
  { id: 'm26', label: 'Consolidated financial statements', source: 'financial', x: 150, y: 350, size: 10 },
  { id: 'm27', label: 'Tax planning strategy 2025', source: 'financial', x: 100, y: 420, size: 8 },
  { id: 'm28', label: 'Banking covenant compliance', source: 'financial', x: 200, y: 480, size: 7 },
];

const MOCK_EDGES = [
  // Panago internal connections
  { source: 'm1', target: 'm2', weight: 0.85 },
  { source: 'm1', target: 'm3', weight: 0.82 },
  { source: 'm3', target: 'm6', weight: 0.88 },
  { source: 'm4', target: 'm5', weight: 0.72 },
  { source: 'm2', target: 'm4', weight: 0.68 },
  // Phantom internal connections
  { source: 'm7', target: 'm8', weight: 0.86 },
  { source: 'm8', target: 'm10', weight: 0.74 },
  { source: 'm9', target: 'm11', weight: 0.79 },
  { source: 'm11', target: 'm12', weight: 0.81 },
  { source: 'm7', target: 'm10', weight: 0.65 },
  // Apex internal connections
  { source: 'm13', target: 'm14', weight: 0.88 },
  { source: 'm14', target: 'm18', weight: 0.76 },
  { source: 'm15', target: 'm17', weight: 0.72 },
  { source: 'm16', target: 'm17', weight: 0.69 },
  { source: 'm13', target: 'm18', weight: 0.84 },
  // Holdco connections (links to all OpCos)
  { source: 'm19', target: 'm1', weight: 0.75 },
  { source: 'm19', target: 'm7', weight: 0.73 },
  { source: 'm19', target: 'm13', weight: 0.78 },
  { source: 'm20', target: 'm22', weight: 0.82 },
  { source: 'm21', target: 'm19', weight: 0.68 },
  // Cross-OpCo synergy connections
  { source: 'm5', target: 'm17', weight: 0.55 }, // Supply chain overlap
  { source: 'm3', target: 'm9', weight: 0.48 }, // Digital/tech growth
  // Market intelligence connections
  { source: 'm23', target: 'm1', weight: 0.62 },
  { source: 'm24', target: 'm7', weight: 0.58 },
  { source: 'm25', target: 'm13', weight: 0.64 },
  { source: 'm23', target: 'm24', weight: 0.71 },
  // Financial connections
  { source: 'm26', target: 'm19', weight: 0.92 },
  { source: 'm27', target: 'm26', weight: 0.85 },
  { source: 'm28', target: 'm26', weight: 0.78 },
  { source: 'm22', target: 'm27', weight: 0.66 },
];

// Source stats for the Sources view - Rooke business data sources
const MOCK_SOURCES = [
  { id: 'panago', label: 'Panago Pizza', count: 1847, color: '#dc2626', percentage: 28 },
  { id: 'phantom', label: 'Phantom Screens', count: 1523, color: '#2563eb', percentage: 23 },
  { id: 'apex', label: 'Apex Food Source', count: 1289, color: '#16a34a', percentage: 20 },
  { id: 'holdco', label: 'Holdco / Corporate', count: 892, color: '#6366f1', percentage: 14 },
  { id: 'market', label: 'Market Intelligence', count: 534, color: '#f59e0b', percentage: 8 },
  { id: 'financial', label: 'Financial Systems', count: 456, color: '#06b6d4', percentage: 7 },
];

// Timeline mock data
const MOCK_TIMELINE = [
  { date: '2025-12-01', count: 45 },
  { date: '2025-12-02', count: 62 },
  { date: '2025-12-03', count: 38 },
  { date: '2025-12-04', count: 71 },
  { date: '2025-12-05', count: 55 },
  { date: '2025-12-06', count: 23 },
  { date: '2025-12-07', count: 18 },
  { date: '2025-12-08', count: 67 },
  { date: '2025-12-09', count: 82 },
  { date: '2025-12-10', count: 59 },
  { date: '2025-12-11', count: 94 },
  { date: '2025-12-12', count: 78 },
  { date: '2025-12-13', count: 31 },
  { date: '2025-12-14', count: 25 },
  { date: '2025-12-15', count: 88 },
  { date: '2025-12-16', count: 103 },
  { date: '2025-12-17', count: 91 },
  { date: '2025-12-18', count: 76 },
];

const SOURCE_COLORS: Record<string, string> = {
  panago: '#dc2626',
  phantom: '#2563eb',
  apex: '#16a34a',
  holdco: '#6366f1',
  market: '#f59e0b',
  financial: '#06b6d4',
};

type ViewType = 'memories' | 'sources' | 'timeline';

export default function MemoryGraphPage() {
  const [currentView, setCurrentView] = useState<ViewType>('sources');
  const [nodeLimit, setNodeLimit] = useState(100);
  const [neighbors, setNeighbors] = useState(5);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nodes, setNodes] = useState<GraphNode[]>(() =>
    INITIAL_NODES.map(n => ({ ...n }))
  );

  // Simulate loading
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, [currentView]);

  const handleRefresh = () => {
    setIsLoading(true);
    setNodes(INITIAL_NODES.map(n => ({ ...n })));
    setTimeout(() => setIsLoading(false), 600);
  };

  const handleNodeDrag = useCallback((nodeId: string, newX: number, newY: number) => {
    setNodes(prev => prev.map(node =>
      node.id === nodeId ? { ...node, x: newX, y: newY } : node
    ));
  }, []);

  const stats = {
    nodes: nodes.length,
    edges: MOCK_EDGES.length,
    clusters: 6,
    totalMemories: 6541,
    generated: new Date().toLocaleTimeString(),
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] text-[#1e293b]" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      {/* Top Navigation */}
      <TopNav />

      {/* Control Bar */}
      <div className="bg-white px-5 py-2 flex items-center justify-between border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-600">View:</span>
          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            {(['sources', 'memories', 'timeline'] as ViewType[]).map((view) => (
              <button
                key={view}
                onClick={() => setCurrentView(view)}
                className={`px-4 py-1.5 rounded-md text-sm transition-all ${
                  currentView === view
                    ? 'bg-[#6366f1] text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {view.charAt(0).toUpperCase() + view.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {currentView === 'memories' && (
            <>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">Nodes:</label>
                <input
                  type="number"
                  value={nodeLimit}
                  onChange={(e) => setNodeLimit(Number(e.target.value))}
                  min={20}
                  max={500}
                  step={20}
                  className="w-20 bg-white border border-gray-300 text-gray-900 px-2 py-1.5 rounded-md text-sm"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600">Neighbors (K):</label>
                <input
                  type="range"
                  value={neighbors}
                  onChange={(e) => setNeighbors(Number(e.target.value))}
                  min={2}
                  max={10}
                  className="w-20"
                />
                <span className="text-sm text-gray-900 w-4">{neighbors}</span>
              </div>
            </>
          )}
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 bg-[#6366f1] text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-[#4f46e5] transition-colors"
          >
            <IconRefresh size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Main Content - Graph takes most space, stats at bottom */}
      <div className="flex flex-col" style={{ height: 'calc(100vh - 104px)' }}>
        {/* Graph Area */}
        <div className="flex-1 relative">
          {/* Loading Overlay */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-50 bg-white/80">
              <div className="text-center text-gray-600">
                <div
                  className="w-10 h-10 border-3 border-gray-200 border-t-[#6366f1] rounded-full mx-auto mb-4"
                  style={{
                    borderWidth: '3px',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <div>Loading business intelligence...</div>
              </div>
            </div>
          )}

          {/* Graph View */}
          {currentView === 'memories' && !isLoading && (
            <MemoriesView
              nodes={nodes}
              edges={MOCK_EDGES}
              selectedNode={selectedNode}
              onSelectNode={setSelectedNode}
              onNodeDrag={handleNodeDrag}
            />
          )}

          {/* Sources View */}
          {currentView === 'sources' && !isLoading && (
            <SourcesView sources={MOCK_SOURCES} />
          )}

          {/* Timeline View */}
          {currentView === 'timeline' && !isLoading && (
            <TimelineView data={MOCK_TIMELINE} sources={MOCK_SOURCES} />
          )}

          {/* Detail Panel - only on right side now */}
          {selectedNode && currentView === 'memories' && (
            <div className="absolute top-4 right-4 bg-white/95 border border-gray-200 rounded-lg p-4 max-w-[350px] z-10 shadow-lg">
              <button
                onClick={() => setSelectedNode(null)}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 text-lg"
              >
                ×
              </button>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Memory Details</h3>
              <div className="text-sm text-gray-700 leading-relaxed mb-3">
                {selectedNode.label}
              </div>
              <div className="text-xs text-gray-500 pt-3 border-t border-gray-200 space-y-1">
                <div><strong>Source:</strong> {selectedNode.source.charAt(0).toUpperCase() + selectedNode.source.slice(1)}</div>
                <div><strong>Connections:</strong> {MOCK_EDGES.filter(e => e.source === selectedNode.id || e.target === selectedNode.id).length}</div>
                <div><strong>ID:</strong> {selectedNode.id}</div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Stats Bar - consolidated stats and legend */}
        {currentView === 'memories' && !isLoading && (
          <div className="bg-white border-t border-gray-200 px-4 py-3 shadow-sm">
            <div className="flex items-center justify-between">
              {/* Graph Stats */}
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Visible Nodes:</span>
                  <span className="text-sm text-gray-900 font-medium">{stats.nodes}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Connections:</span>
                  <span className="text-sm text-gray-900 font-medium">{stats.edges}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">OpCo Clusters:</span>
                  <span className="text-sm text-gray-900 font-medium">{stats.clusters}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Total Memories:</span>
                  <span className="text-sm text-gray-900 font-medium">{stats.totalMemories.toLocaleString()}</span>
                </div>
              </div>

              {/* Source Legend */}
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">Sources:</span>
                {Object.entries(SOURCE_COLORS).map(([source, color]) => (
                  <div key={source} className="flex items-center gap-1.5">
                    <span
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-xs text-gray-600">
                      {source.charAt(0).toUpperCase() + source.slice(1)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Drag hint */}
              <div className="text-xs text-gray-400 italic">
                Drag nodes to rearrange • Click to select
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Memories Graph View Component with Dragging
function MemoriesView({
  nodes,
  edges,
  selectedNode,
  onSelectNode,
  onNodeDrag
}: {
  nodes: GraphNode[];
  edges: typeof MOCK_EDGES;
  selectedNode: GraphNode | null;
  onSelectNode: (node: GraphNode | null) => void;
  onNodeDrag: (nodeId: string, x: number, y: number) => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [draggingNode, setDraggingNode] = useState<string | null>(null);
  const dragStartPos = useRef<{ x: number; y: number; nodeX: number; nodeY: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent, node: GraphNode) => {
    e.preventDefault();
    setDraggingNode(node.id);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      nodeX: node.x,
      nodeY: node.y
    };
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!draggingNode || !dragStartPos.current) return;

    const dx = e.clientX - dragStartPos.current.x;
    const dy = e.clientY - dragStartPos.current.y;

    onNodeDrag(
      draggingNode,
      dragStartPos.current.nodeX + dx,
      dragStartPos.current.nodeY + dy
    );
  }, [draggingNode, onNodeDrag]);

  const handleMouseUp = useCallback(() => {
    setDraggingNode(null);
    dragStartPos.current = null;
  }, []);

  useEffect(() => {
    if (draggingNode) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggingNode, handleMouseMove, handleMouseUp]);

  const handleClick = (node: GraphNode) => {
    if (!draggingNode) {
      onSelectNode(node);
    }
  };

  return (
    <svg
      ref={svgRef}
      className="w-full h-full"
      style={{ background: '#f8f9fa', cursor: draggingNode ? 'grabbing' : 'default' }}
    >
      {/* Edges */}
      {edges.map((edge, i) => {
        const sourceNode = nodes.find(n => n.id === edge.source);
        const targetNode = nodes.find(n => n.id === edge.target);
        if (!sourceNode || !targetNode) return null;

        const isHighlighted = hoveredNode === edge.source || hoveredNode === edge.target ||
                              draggingNode === edge.source || draggingNode === edge.target;

        return (
          <line
            key={i}
            x1={sourceNode.x}
            y1={sourceNode.y}
            x2={targetNode.x}
            y2={targetNode.y}
            stroke={isHighlighted ? '#6366f1' : '#94a3b8'}
            strokeWidth={isHighlighted ? 2 : 1}
            strokeOpacity={isHighlighted ? 0.8 : 0.5}
          />
        );
      })}

      {/* Nodes */}
      {nodes.map((node) => {
        const isHovered = hoveredNode === node.id;
        const isSelected = selectedNode?.id === node.id;
        const isDragging = draggingNode === node.id;
        const color = SOURCE_COLORS[node.source] || '#6366f1';

        return (
          <g
            key={node.id}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            onMouseEnter={() => !draggingNode && setHoveredNode(node.id)}
            onMouseLeave={() => !draggingNode && setHoveredNode(null)}
            onMouseDown={(e) => handleMouseDown(e, node)}
            onClick={() => handleClick(node)}
          >
            {/* Glow effect on hover/drag */}
            {(isHovered || isSelected || isDragging) && (
              <circle
                cx={node.x}
                cy={node.y}
                r={node.size + 6}
                fill={color}
                opacity={isDragging ? 0.5 : 0.3}
              />
            )}
            {/* Main node */}
            <circle
              cx={node.x}
              cy={node.y}
              r={node.size}
              fill={color}
              stroke={isSelected || isDragging ? '#fff' : color}
              strokeWidth={isSelected || isDragging ? 2 : 1}
            />
            {/* Label on hover */}
            {(isHovered || isDragging) && (
              <>
                {/* Background for better readability */}
                <rect
                  x={node.x - (Math.min(node.label.length, 30) * 3.5)}
                  y={node.y - node.size - 22}
                  width={Math.min(node.label.length, 30) * 7}
                  height={18}
                  fill="white"
                  rx={4}
                  style={{ pointerEvents: 'none' }}
                />
                <text
                  x={node.x}
                  y={node.y - node.size - 8}
                  textAnchor="middle"
                  fill="#1e293b"
                  fontSize="11"
                  fontWeight="500"
                  style={{ pointerEvents: 'none' }}
                >
                  {node.label.length > 30 ? node.label.substring(0, 30) + '...' : node.label}
                </text>
              </>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// Sources View Component
function SourcesView({ sources }: { sources: typeof MOCK_SOURCES }) {
  const centerX = 450;
  const centerY = 250;
  const maxRadius = 180;

  return (
    <div className="w-full h-full flex items-center justify-center">
      <svg width="900" height="500" style={{ background: '#f8f9fa' }}>
        {/* Source nodes in a circle */}
        {sources.map((source, i) => {
          const angle = (i / sources.length) * 2 * Math.PI - Math.PI / 2;
          const radius = maxRadius;
          const x = centerX + radius * Math.cos(angle);
          const y = centerY + radius * Math.sin(angle);
          const nodeSize = 20 + (source.percentage / 5);

          return (
            <g key={source.id}>
              {/* Connection to center */}
              <line
                x1={centerX}
                y1={centerY}
                x2={x}
                y2={y}
                stroke={source.color}
                strokeWidth={2}
                strokeOpacity={0.4}
              />
              {/* Node */}
              <circle
                cx={x}
                cy={y}
                r={nodeSize}
                fill={source.color}
              />
              {/* Label */}
              <text
                x={x}
                y={y + nodeSize + 20}
                textAnchor="middle"
                fill="#1e293b"
                fontSize="13"
                fontWeight="600"
              >
                {source.label}
              </text>
              {/* Count */}
              <text
                x={x}
                y={y + nodeSize + 36}
                textAnchor="middle"
                fill="#64748b"
                fontSize="11"
              >
                {source.count.toLocaleString()} memories
              </text>
            </g>
          );
        })}

        {/* Center node */}
        <circle
          cx={centerX}
          cy={centerY}
          r={35}
          fill="#6366f1"
        />
        <text
          x={centerX}
          y={centerY - 5}
          textAnchor="middle"
          fill="#fff"
          fontSize="11"
          fontWeight="600"
        >
          Total
        </text>
        <text
          x={centerX}
          y={centerY + 10}
          textAnchor="middle"
          fill="#fff"
          fontSize="13"
          fontWeight="700"
        >
          {sources.reduce((sum, s) => sum + s.count, 0).toLocaleString()}
        </text>
      </svg>

      {/* Stats Panel */}
      <div className="absolute top-4 left-4 bg-white/95 border border-gray-200 rounded-lg p-4 min-w-[250px] shadow-lg">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">OpCo Data Distribution</h3>
        <div className="space-y-3">
          {sources.map((source) => (
            <div key={source.id}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-700">{source.label}</span>
                <span className="text-gray-500">{source.percentage}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${source.percentage}%`,
                    backgroundColor: source.color
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Timeline View Component
function TimelineView({ data, sources }: { data: typeof MOCK_TIMELINE; sources: typeof MOCK_SOURCES }) {
  const maxCount = Math.max(...data.map(d => d.count));
  const totalMemories = data.reduce((sum, d) => sum + d.count, 0);
  const avgPerDay = Math.round(totalMemories / data.length);

  return (
    <div className="w-full h-full p-5 overflow-y-auto bg-[#f8f9fa]">
      {/* Bar Chart */}
      <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
        <h3 className="text-gray-900 font-semibold mb-3">Business Intelligence Activity Over Time</h3>
        <div className="flex items-end gap-1 h-48">
          {data.map((d, i) => {
            const height = Math.max(8, (d.count / maxCount) * 180);
            return (
              <div
                key={i}
                className="flex-1 min-w-[8px] max-w-[24px] rounded-t cursor-pointer hover:opacity-80 transition-opacity"
                style={{
                  height: `${height}px`,
                  background: 'linear-gradient(to top, #6366f1, #8b5cf6)'
                }}
                title={`${new Date(d.date).toLocaleDateString()}: ${d.count} data points`}
              />
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-2">
          <span>{new Date(data[0].date).toLocaleDateString()}</span>
          <span>{new Date(data[data.length - 1].date).toLocaleDateString()}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Source breakdown */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h3 className="text-gray-900 font-semibold mb-3">Data by OpCo</h3>
          <div className="space-y-3">
            {sources.slice(0, 6).map((source) => {
              const width = (source.count / sources[0].count) * 100;
              return (
                <div key={source.id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-700">{source.label}</span>
                    <span className="text-gray-500">{source.count.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${width}%`,
                        backgroundColor: source.color
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity Heatmap */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
          <h3 className="text-gray-900 font-semibold mb-3">Activity by Day of Week</h3>
          <div className="grid grid-cols-7 gap-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
              const count = [67, 82, 59, 94, 78, 31, 25][i];
              const intensity = count / 94;
              return (
                <div
                  key={day}
                  className="text-center p-3 rounded"
                  style={{ background: `rgba(99, 102, 241, ${0.15 + intensity * 0.5})` }}
                >
                  <div className="text-xs text-gray-600">{day}</div>
                  <div className="text-sm text-gray-900 font-semibold">{count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <h3 className="text-gray-900 font-semibold mb-3">Summary Statistics</h3>
        <div className="flex gap-8">
          <div>
            <span className="text-gray-500 text-sm">Total Data Points:</span>
            <span className="text-gray-900 font-semibold ml-2">{totalMemories.toLocaleString()}</span>
          </div>
          <div>
            <span className="text-gray-500 text-sm">Days Tracked:</span>
            <span className="text-gray-900 font-semibold ml-2">{data.length}</span>
          </div>
          <div>
            <span className="text-gray-500 text-sm">Peak Activity:</span>
            <span className="text-gray-900 font-semibold ml-2">{maxCount}</span>
          </div>
          <div>
            <span className="text-gray-500 text-sm">Avg/Day:</span>
            <span className="text-gray-900 font-semibold ml-2">{avgPerDay}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
